// Auth store for managing authentication state (identity2 / Better Auth).
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { authService } from '@/services/auth'
import type { AuthTokens, AuthUser } from '@/config/auth2'
import { captureSentryException, addSentryBreadcrumb } from '@/config/sentry'

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const user = ref<AuthUser | null>(null)
  const tokens = ref<AuthTokens | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters — a single JWT backs both access and id token slots (callers decode it).
  const isLoggedIn = computed(() => isAuthenticated.value && user.value !== null)
  const accessToken = computed(() => tokens.value?.token || null)
  const idToken = computed(() => tokens.value?.token || null)
  const refreshToken = computed(() => null) // no separate refresh token in the Better Auth model
  const userEmail = computed(() => user.value?.email || null)
  const userName = computed(() => user.value?.name || null)

  const setLoading = (state: boolean) => { loading.value = state }
  const setError = (errorMessage: string | null) => { error.value = errorMessage }

  const setAuthenticated = (authTokens: AuthTokens) => {
    try {
      const userData = authService.userFromToken(authTokens.token)
      isAuthenticated.value = true
      tokens.value = authTokens
      user.value = userData
      error.value = null

      localStorage.setItem('auth_tokens', JSON.stringify(authTokens))
      localStorage.setItem('user_info', JSON.stringify(userData))

      addSentryBreadcrumb(`User authenticated: ${userData.email}`, 'auth')
    } catch (err) {
      captureSentryException(err as Error, { store: 'AuthStore', action: 'setAuthenticated' })
      throw err
    }
  }

  const clearAuthentication = () => {
    try {
      isAuthenticated.value = false
      tokens.value = null
      user.value = null
      error.value = null
      localStorage.removeItem('auth_tokens')
      localStorage.removeItem('user_info')
      addSentryBreadcrumb('User authentication cleared', 'auth')
    } catch (err) {
      captureSentryException(err as Error, { store: 'AuthStore', action: 'clearAuthentication' })
    }
  }

  const login = async (registry: string = 'sor') => {
    try {
      setLoading(true)
      setError(null)
      addSentryBreadcrumb(`Initiating login for registry: ${registry}`, 'auth')
      authService.redirectToLogin(registry)
    } catch (err) {
      const errorValue = err as Error
      setError(errorValue.message)
      captureSentryException(errorValue, { store: 'AuthStore', action: 'login', registry })
      throw errorValue
    } finally {
      setLoading(false)
    }
  }

  /** Completes the redirect flow by reading the JWT from the callback fragment. */
  const handleAuthCallback = async () => {
    try {
      setLoading(true)
      setError(null)
      addSentryBreadcrumb('Processing auth callback', 'auth')

      const authTokens = authService.handleCallback()
      setAuthenticated(authTokens)

      addSentryBreadcrumb('Auth callback processed successfully', 'auth')
      return { success: true }
    } catch (err) {
      const errorValue = err as Error
      setError(errorValue.message)
      clearAuthentication()
      captureSentryException(errorValue, { store: 'AuthStore', action: 'handleAuthCallback' })
      return { success: false, error: errorValue.message }
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      setLoading(true)
      addSentryBreadcrumb('User logging out', 'auth')
      clearAuthentication()
      authService.logout()
    } catch (err) {
      const errorValue = err as Error
      setError(errorValue.message)
      captureSentryException(errorValue, { store: 'AuthStore', action: 'logout' })
      throw errorValue
    } finally {
      setLoading(false)
    }
  }

  const refreshTokens = async () => {
    try {
      setLoading(true)
      addSentryBreadcrumb('Refreshing tokens', 'auth')

      const newTokens = await authService.refreshTokens()
      tokens.value = newTokens
      if (!user.value) {
        user.value = authService.userFromToken(newTokens.token)
      }
      localStorage.setItem('auth_tokens', JSON.stringify(newTokens))

      addSentryBreadcrumb('Tokens refreshed successfully', 'auth')
      return { success: true }
    } catch (err) {
      const errorValue = err as Error
      setError(errorValue.message)
      clearAuthentication()
      authService.redirectToLogin()
      return { success: false, error: errorValue.message, shouldRedirect: true }
    } finally {
      setLoading(false)
    }
  }

  const checkAuthState = async () => {
    try {
      setLoading(true)
      addSentryBreadcrumb('Checking auth state', 'auth')

      const authState = await authService.validateAuthState()

      if (authState.isAuthenticated && authState.user) {
        const storedTokens = localStorage.getItem('auth_tokens')
        if (storedTokens) {
          tokens.value = JSON.parse(storedTokens)
          user.value = authState.user
          isAuthenticated.value = true
          addSentryBreadcrumb(`Auth state restored for: ${authState.user.email}`, 'auth')
        }
      } else {
        clearAuthentication()
      }

      return authState.isAuthenticated
    } catch (err) {
      const errorValue = err as Error
      setError(errorValue.message)
      clearAuthentication()
      captureSentryException(errorValue, { store: 'AuthStore', action: 'checkAuthState' })
      return false
    } finally {
      setLoading(false)
    }
  }

  const isTokenExpired = () => {
    if (!tokens.value?.token) {
      return true
    }
    return authService.isTokenExpired(tokens.value.token)
  }

  const getValidAccessToken = async (): Promise<string | null> => {
    try {
      if (!tokens.value?.token) {
        return null
      }
      if (isTokenExpired()) {
        const result = await refreshTokens()
        if (!result.success) {
          return null
        }
      }
      return tokens.value?.token || null
    } catch (err) {
      captureSentryException(err as Error, { store: 'AuthStore', action: 'getValidAccessToken' })
      return null
    }
  }

  // Same underlying JWT as the access token in the Better Auth model.
  const getValidIdToken = async (): Promise<string | null> => {
    return getValidAccessToken()
  }

  return {
    // State
    isAuthenticated,
    user,
    tokens,
    loading,
    error,

    // Getters
    isLoggedIn,
    accessToken,
    idToken,
    refreshToken,
    userEmail,
    userName,

    // Actions
    login,
    logout,
    handleAuthCallback,
    refreshTokens,
    checkAuthState,
    clearAuthentication,
    isTokenExpired,
    getValidAccessToken,
    getValidIdToken,
    setLoading,
    setError,
  }
})
